/**
 * GenerationPicker — Explore-topbar control for the cluster
 * generation pin (lib/generationPin.js). Sits next to the
 * DateRangePicker because it's the same kind of thing: a global
 * query modifier every chart, kernel, and chip in this browser
 * respects. Living inside Explore (rather than the Settings stack)
 * also makes an ACTIVE pin visible at the point where it changes the
 * numbers — a pinned trigger renders green so nobody puzzles over
 * cluster sets that don't match a colleague's screen.
 *
 * Interaction model mirrors DateRangePicker: portal'd fixed-position
 * menu (the topbar has overflow:hidden for the collapse animation),
 * click-outside + Escape to close. Generations are fetched lazily on
 * first open via ENTITY_TYPE_ILO_CLUSTER_GENERATION — before the
 * O-1547 backend deploys that query fails and the menu shows the
 * error instead of options.
 *
 * Selecting writes the pin and applies IN PLACE — no reload.
 * generationPin fires its change channel; the cache modules clear
 * themselves and dataClient bumps its data-epoch, so open views
 * refetch through the new generation while scroll position, open
 * columns, and panel layout are preserved.
 *
 * Algorithm filter: a chip row narrows the list to one algorithm
 * (e.g. leiden_hier). The chip set comes from a separate facet query
 * grouped by algorithm — NOT from the 25-row listing — so a rarely
 * run algorithm still shows up as a chip even when frequent HDBSCAN
 * runs would crowd it out of the recent list. Selecting a chip
 * refetches the list filtered server-side, so you always see the most
 * recent generations OF THAT algorithm.
 *
 * Visible to tenant admins (it's an admin/debug tool) and in local dev
 * (no auth → no roles), and always when a pin is set so it can be cleared.
 * See generationPickerVisible.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "../lib/dataClient.js";
import { GEN_PARAM, generationLaunchVisible, generationPickerVisible, notifyGenerationPinChange } from "../lib/generationPin.js";
import { readUrlParams } from "../lib/urlParams.js";
import { useCurrentUser } from "../lib/userInfo.js";
import { AUTH_REQUIRED } from "../lib/featureFlags.js";
import { isSurfaceDenied, useMyPermissions } from "../lib/permissions.js";
import { ALGORITHMS, DEFAULT_CONF_CUT, isSteerable, parseTaxonomy, triggerIloRebuild } from "../lib/iloRebuild.js";
import HoverTooltip from "./HoverTooltip.jsx";
import Select from "./Select.jsx";
import { listCensusGenerations, triggerCensusSweep } from "../lib/censusClient.js";
import RegisterBuildsPanel from "./RegisterBuildsPanel.jsx";
import { NOTE_CLASS } from "../lib/ui.js";

// Generation reads (list + algorithm facets) now live as registered analytics
// methods in apiClient.js (getGenerations / getGenerationAlgorithms), consumed
// via useQuery below — this component no longer builds specs or calls the wire.
const ALL = "all";

function fmtGenerationDate(ts) {
    if (!ts) return "—";
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString(undefined, {
        month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
}

// Layered-carve glyph: three stacked strata, the generation metaphor.
function GenerationIcon() {
    return (
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M8 2.5 14 5.5 8 8.5 2 5.5Z" />
            <path d="M2.5 8.75 8 11.5l5.5-2.75" />
            <path d="M2.5 11.75 8 14.5l5.5-2.75" />
        </svg>
    );
}

function AlgoChip({ label, active, onSelect }) {
    return (
        <button
            type="button"
            onClick={onSelect}
            className={[
                "shrink-0 rounded-[2px] border px-[8px] py-[3px] text-[11px] leading-[1.1] cursor-pointer transition-colors",
                active
                    ? "border-[color:var(--green)] bg-[color:var(--green-soft)] text-[color:var(--ink)] font-medium"
                    : "border-[color:var(--line)] text-[color:var(--muted)] hover:text-[color:var(--ink)] hover:border-[color:var(--line-strong)]",
            ].join(" ")}
        >
            {label}
        </button>
    );
}

function MenuRow({ title, note, active, disabled, onSelect }) {
    return (
        <button
            type="button"
            disabled={disabled}
            onClick={onSelect}
            className={[
                "grid gap-[3px] border-0 border-t border-[color:var(--line)] bg-transparent px-[10px] py-2 text-left transition-colors",
                active
                    ? "relative !bg-[color:var(--green-soft)] before:absolute before:inset-y-0 before:left-0 before:w-[2px] before:bg-[color:var(--green)]"
                    : disabled
                        ? "text-[color:var(--muted-soft)] opacity-60"
                        : "cursor-pointer hover:bg-[color:var(--control-bg)]",
            ].join(" ")}
        >
            <strong className="truncate text-[12px] leading-[1.12] font-medium">{title}</strong>
            {note ? <div className={`${NOTE_CLASS} truncate`}>{note}</div> : null}
        </button>
    );
}

const FIELD_CLASS =
    "w-full rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[6px] py-[4px] text-[12px] text-[color:var(--ink)] outline-none focus:border-[color:var(--line-strong)]";

const ALGO_OPTIONS = ALGORITHMS.map((a) => ({ value: a.value, label: a.label }));

function TabButton({ label, active, onSelect }) {
    return (
        <button
            type="button"
            onClick={onSelect}
            className={[
                "border-0 bg-transparent px-[10px] py-[6px] text-[12px] leading-[1.1] cursor-pointer transition-colors",
                active
                    ? "font-medium text-[color:var(--ink)] shadow-[inset_0_-2px_0_var(--green)]"
                    : "text-[color:var(--muted)] hover:text-[color:var(--ink)]",
            ].join(" ")}
        >
            {label}
        </button>
    );
}

/// One census reading row: informational (readings are not pinnable from
/// here; the newest completed one is what census surfaces serve by default).
function CensusRow({ generation, current }) {
    const completed = generation.status === "completed";
    // Session counts are stamped at completion; a running or failed row
    // showing "0 sessions" would read as an empty sweep.
    const note = [
        completed ? null : generation.status,
        completed ? `${Number(generation.sessionsInWindow || 0).toLocaleString()} sessions` : null,
        completed ? fmtGenerationDate(generation.completedAt) : null,
        current ? "current" : null,
    ]
        .filter(Boolean)
        .join(" · ");
    return (
        <div
            className={[
                "grid gap-[3px] border-t border-[color:var(--line)] px-[10px] py-2",
                current
                    ? "relative bg-[color:var(--green-soft)] before:absolute before:inset-y-0 before:left-0 before:w-[2px] before:bg-[color:var(--green)]"
                    : "",
                generation.status === "failed" ? "opacity-60" : "",
            ].join(" ")}
        >
            <strong className="truncate text-[12px] leading-[1.12] font-medium">
                {`Reading · ${String(generation.rebuildId).slice(0, 8)}…`}
            </strong>
            <div className={`${NOTE_CLASS} truncate`}>{note}</div>
        </div>
    );
}

/// The Census tab: the tenant's readings plus the trigger. A reading is a
/// point-in-time map of the work happening across sessions; the newest
/// completed one backs the session panels and initiative reads.
function CensusPanel({ canManage }) {
    const [state, setState] = useState({ status: "loading", generations: [], pinned: "" });
    const [notice, setNotice] = useState(null);
    const [problem, setProblem] = useState(null);
    const [starting, setStarting] = useState(false);
    const [tick, setTick] = useState(0);

    useEffect(() => {
        let alive = true;
        listCensusGenerations({ pageSize: 8 })
            .then((res) => {
                if (!alive) return;
                setState({ status: "ready", generations: res.generations, pinned: res.pinnedRebuildId });
            })
            .catch((err) => {
                if (!alive) return;
                setState({
                    status: "error",
                    generations: [],
                    pinned: "",
                    message: err?.serverMessage || err?.message || String(err),
                });
            });
        return () => { alive = false; };
    }, [tick]);

    // A queued or running reading resolves out-of-band (the census worker runs
    // every 15 minutes): poll while one is visible.
    const pending = state.generations.some((g) => g.status === "queued" || g.status === "running");
    useEffect(() => {
        if (!pending) return undefined;
        const t = setTimeout(() => setTick((n) => n + 1), 15000);
        return () => clearTimeout(t);
    }, [pending, tick]);

    const start = async () => {
        setStarting(true);
        setNotice(null);
        setProblem(null);
        try {
            await triggerCensusSweep({});
            setNotice("Census reading queued. The census worker picks it up within about 15 minutes.");
            setTick((n) => n + 1);
        } catch (err) {
            setProblem(err?.serverMessage || err?.message || String(err));
        } finally {
            setStarting(false);
        }
    };

    return (
        <>
            <div className="grid gap-[2px] px-[10px] py-2">
                <span className={NOTE_CLASS}>
                    Point-in-time maps of the work happening across sessions. The newest
                    completed reading backs the Initiatives panels.
                </span>
            </div>
            {notice ? (
                <div className="border-t border-[color:var(--line)] px-[10px] py-2">
                    <span className="text-[11px] text-[color:var(--green)]">{notice}</span>
                </div>
            ) : null}
            {problem ? (
                <div className="border-t border-[color:var(--line)] px-[10px] py-2">
                    <span className="text-[11px] text-red-400">{problem}</span>
                </div>
            ) : null}
            {canManage ? (
                <button
                    type="button"
                    disabled={starting}
                    onClick={start}
                    className="border-0 border-t border-[color:var(--line)] bg-transparent px-[10px] py-2 text-left text-[12px] font-medium text-[color:var(--muted)] transition-colors hover:bg-[color:var(--control-bg)] hover:text-[color:var(--ink)] cursor-pointer"
                >
                    {starting ? "Starting…" : "+ New census reading"}
                </button>
            ) : null}
            {state.status === "error" ? (
                <div className="border-t border-[color:var(--line)] px-[10px] py-2">
                    <span className="text-[11px] text-red-400">{state.message}</span>
                    <div className={NOTE_CLASS}>Census readings are not available on this server.</div>
                </div>
            ) : state.status === "loading" ? (
                <div className={`${NOTE_CLASS} border-t border-[color:var(--line)] px-[10px] py-2`}>Loading readings…</div>
            ) : (
                <>
                    {state.generations.map((g) => (
                        <CensusRow key={g.rebuildId} generation={g} current={Boolean(state.pinned) && g.rebuildId === state.pinned} />
                    ))}
                    {state.generations.length === 0 ? (
                        <div className={`${NOTE_CLASS} border-t border-[color:var(--line)] px-[10px] py-2`}>
                            No census readings for this tenant yet. Run one to map current work.
                        </div>
                    ) : null}
                </>
            )}
            <RegisterBuildsPanel canManage={canManage} />
        </>
    );
}

// Inline "launch a new clustering pass" form, shown at the top of the
// menu for admins. Steering (probe texts) is the
// headline — only null_merge consumes them — but any algorithm can be
// launched with server-config defaults. Self-contained: owns its own
// form state and reports the launched (algorithm, id) up via onLaunched.
function ComposePanel({ onLaunched }) {
    const [expanded, setExpanded] = useState(false);
    const [algorithm, setAlgorithm] = useState("null_merge");
    const [probesText, setProbesText] = useState("");
    const [confCut, setConfCut] = useState(String(DEFAULT_CONF_CUT));
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const steerable = isSteerable(algorithm);
    const { probes, parent } = parseTaxonomy(probesText);
    // Steering is optional even on a steerable algorithm: an empty probe set launches an unguided
    // (plain FOSC) null_merge pass. buildRebuildRequest omits the steering blob when probes is empty.
    const canSubmit = !submitting;

    const launch = async () => {
        setSubmitting(true);
        setError(null);
        try {
            const res = await triggerIloRebuild({ algorithm, probes, parent, confCut: Number(confCut) });
            setExpanded(false);
            setProbesText("");
            onLaunched(algorithm, res?.rebuildId || res?.rebuild_id || "");
        } catch (err) {
            setError(err?.serverMessage || err?.message || String(err));
        } finally {
            setSubmitting(false);
        }
    };

    if (!expanded) {
        return (
            <button
                type="button"
                onClick={() => setExpanded(true)}
                className="border-0 border-t border-[color:var(--line)] bg-transparent px-[10px] py-2 text-left text-[12px] font-medium text-[color:var(--muted)] transition-colors hover:bg-[color:var(--control-bg)] hover:text-[color:var(--ink)] cursor-pointer"
            >
                + New topic generation
            </button>
        );
    }

    return (
        <div className="grid gap-[8px] border-t border-[color:var(--line)] px-[10px] py-[10px]">
            <label className="grid gap-[3px]">
                <span className={NOTE_CLASS}>Algorithm</span>
                <Select value={algorithm} onChange={setAlgorithm} options={ALGO_OPTIONS} ariaLabel="Topic generation algorithm" />
            </label>

            {steerable ? (
                <>
                    <label className="grid gap-[3px]">
                        <span className={NOTE_CLASS}>Attributes to steer toward</span>
                        <textarea
                            className={`${FIELD_CLASS} min-h-[64px] resize-y font-mono text-[11px] leading-[1.35]`}
                            placeholder={"security\n  malware\n  exploit dev\nhr\n  recruiting"}
                            value={probesText}
                            onChange={(e) => setProbesText(e.target.value)}
                        />
                        <span className={NOTE_CLASS}>Enter one attribute per line. Indent child attributes under their parent.</span>
                    </label>
                    <label className="grid grid-cols-[auto_72px] items-center gap-[6px]">
                        <span className={NOTE_CLASS}>Confidence cut</span>
                        <input
                            type="number"
                            className={FIELD_CLASS}
                            min="0.5"
                            max="5"
                            step="0.1"
                            value={confCut}
                            onChange={(e) => setConfCut(e.target.value)}
                        />
                    </label>
                </>
            ) : (
                <span className={NOTE_CLASS}>Runs with the server-configured defaults for this algorithm.</span>
            )}

            {error ? <span className="text-[11px] text-red-400">{error}</span> : null}

            <div className="flex items-center gap-[6px]">
                <button
                    type="button"
                    disabled={!canSubmit}
                    onClick={launch}
                    className={[
                        "rounded-[2px] border px-[10px] py-[4px] text-[12px] font-medium transition-colors",
                        canSubmit
                            ? "cursor-pointer border-[color:var(--green)] bg-[color:var(--green-soft)] text-[color:var(--ink)] hover:brightness-110"
                            : "border-[color:var(--line)] text-[color:var(--muted-soft)] opacity-60",
                    ].join(" ")}
                >
                    {submitting ? "Launching…" : "Launch pass"}
                </button>
                <button
                    type="button"
                    disabled={submitting}
                    onClick={() => { setExpanded(false); setError(null); }}
                    className="rounded-[2px] border border-[color:var(--line)] bg-transparent px-[10px] py-[4px] text-[12px] text-[color:var(--muted)] transition-colors hover:text-[color:var(--ink)] cursor-pointer"
                >
                    Cancel
                </button>
                {steerable && probes.length === 0 ? (
                    <span className={NOTE_CLASS}>No attributes selected. This pass will be unguided.</span>
                ) : null}
            </div>
        </div>
    );
}

export default function GenerationPicker() {
    const triggerRef = useRef(null);
    const menuRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [menuPos, setMenuPos] = useState({ left: 0, top: 0, minWidth: 0 });
    // The pin is the `gen` URL param — reactive via useSearchParams, so
    // the trigger color + active row follow it (picker click, a shared
    // link, or back/forward) with no local state to sync.
    const [searchParams, setSearchParams] = useSearchParams();
    const pinnedId = searchParams.get(GEN_PARAM) || null;
    const [selectedAlgo, setSelectedAlgo] = useState(ALL);
    const [launchNotice, setLaunchNotice] = useState(null);
    const [refreshTick, setRefreshTick] = useState(0);
    // "clusters" | "census" -- the menu's two surfaces share the trigger
    // button and the generation metaphor; only clusters are pinnable.
    const [tab, setTab] = useState("clusters");

    // Generation reads go through registered analytics methods + useQuery — one
    // data-access contract, with dedup and cancellation for free — rather than
    // bespoke runQuery effects. enabled:open fetches when the picker opens;
    // staleMs:0 plus the methods' own {cache:false} keep them fresh so a
    // just-completed rebuild shows; refreshTick in the params forces a refetch
    // after a launch.
    const { data: algoFacetsData } = useQuery("getGenerationAlgorithms", {}, { enabled: open, staleMs: 0 });
    const algoFacets = algoFacetsData ?? null;
    const { data: generationsData, loading: listLoading, error: listErr, denied: listDenied } = useQuery(
        "getGenerations", { algorithm: selectedAlgo, refreshTick }, { enabled: open, staleMs: 0 },
    );
    const generations = generationsData ?? null;
    const error = listErr ? (listErr.serverMessage || listErr.message || String(listErr)) : null;

    const user = useCurrentUser();
    const caps = useMyPermissions();
    const pickerDenied = isSurfaceDenied(caps, "GetIloRebuilds");
    const launchDenied = isSurfaceDenied(caps, "TriggerIloRebuild");
    const isTenantAdmin = user?.currentTenant?.role === "TENANT_ROLE_ADMIN";
    const visible = generationPickerVisible({
        isTenantAdmin,
        authRequired: AUTH_REQUIRED,
        pinned: Boolean(pinnedId),
    });
    // Launching is a write into the clustering pipeline — offer it only
    // to the same admin/local-dev surface that may manage generations. A mere
    // pin makes the picker visible to anyone, but does not expose this form.
    const canManage = generationLaunchVisible({ isTenantAdmin, authRequired: AUTH_REQUIRED }) && !launchDenied;

    const recompute = useCallback(() => {
        const trigger = triggerRef.current;
        if (!trigger) return;
        const r = trigger.getBoundingClientRect();
        // Open along the same left baseline as the date selector's
        // menu: both dropdowns hang from the topbar strip, so they
        // share its left edge and the generation menu spans from
        // there to its own trigger's right edge. Fallback (picker
        // rendered outside the topbar) right-aligns to the trigger.
        const bar = trigger.closest("[data-sidebar-topbar]");
        const left = bar ? bar.getBoundingClientRect().left : Math.max(8, r.right - 300);
        setMenuPos({ left, top: r.bottom + 4, minWidth: Math.max(300, r.right - left) });
    }, []);

    useLayoutEffect(() => {
        if (open) recompute();
    }, [open, recompute]);

    useEffect(() => {
        if (!open) return;
        const onScrollResize = () => recompute();
        const onClickOutside = (e) => {
            if (triggerRef.current?.contains(e.target)) return;
            if (menuRef.current?.contains(e.target)) return;
            // A Select opened inside the menu portals its listbox to
            // <body>, outside menuRef. It picks on click but we close on
            // mousedown, so without this an algorithm pick would close
            // the whole picker before the option's click landed.
            if (e.target.closest?.('[role="listbox"]')) return;
            setOpen(false);
        };
        const onKey = (e) => {
            if (e.key === "Escape") setOpen(false);
        };
        window.addEventListener("scroll", onScrollResize, true);
        window.addEventListener("resize", onScrollResize);
        window.addEventListener("mousedown", onClickOutside);
        window.addEventListener("keydown", onKey);
        return () => {
            window.removeEventListener("scroll", onScrollResize, true);
            window.removeEventListener("resize", onScrollResize);
            window.removeEventListener("mousedown", onClickOutside);
            window.removeEventListener("keydown", onKey);
        };
    }, [open, recompute]);

    // A launched pass lands as a "running" row; surface it, switch the
    // filter to its algorithm so it shows, and refetch (tick covers the
    // case where the algorithm filter is already selected).
    const onLaunched = (algo, id) => {
        setLaunchNotice(
            id
                ? `Launched ${algo} pass ${String(id).slice(0, 8)}… Status: running.`
                : `Launched ${algo} pass. It is now running.`,
        );
        setSelectedAlgo(algo);
        setRefreshTick((t) => t + 1);
    };

    // Write the `gen` param via react-router (so the tree re-renders +
    // the change is shareable/back-able), merging onto a FRESH read of
    // the hash so we don't clobber raw-hash params (dcol/xcol). App
    // watches `gen` and fires the cache-invalidation channel.
    const choose = (row) => {
        setSearchParams(() => {
            const next = readUrlParams();
            if (row?.generationId) next.set(GEN_PARAM, row.generationId);
            else next.delete(GEN_PARAM);
            return next;
        });
        setLaunchNotice(null);
        setOpen(false);
    };

    if (!visible) return null;
    if (pickerDenied) {
        if (!pinnedId) return null;
        const clearLabel = `Topic generation ${pinnedId.slice(0, 8)}... is pinned, but generation history is unavailable to this subject-scoped account. Clear the pin.`;
        return (
            <div data-generation-picker className="relative shrink-0">
                <HoverTooltip label={clearLabel} placement="left">
                    <button
                        type="button"
                        aria-label={clearLabel}
                        onClick={() => choose(null)}
                        className="grid h-[38px] w-[34px] shrink-0 place-items-center border-x border-t border-[color:var(--green)] bg-[color:var(--green-soft)] p-0 text-[color:var(--green)] [&_svg]:block [&_svg]:h-[13px] [&_svg]:w-[13px]"
                    >
                        <GenerationIcon />
                    </button>
                </HoverTooltip>
            </div>
        );
    }

    // The URL carries only the id; resolve display metadata from the
    // fetched list when available.
    const pinnedMeta = pinnedId ? (generations || []).find((g) => g.generationId === pinnedId) : null;
    const orphanPin =
        pinnedId && !listLoading && generations !== null && !pinnedMeta
            ? { generationId: pinnedId }
            : null;
    const tooltip = pinnedId
        ? (pinnedMeta
            ? `Topic generation pinned: ${pinnedMeta.algorithm} · ${fmtGenerationDate(pinnedMeta.completedAt)}. This browser will use it for every query.`
            : `Topic generation pinned: ${pinnedId.slice(0, 8)}… This browser will use it for every query.`)
        : "Topic generation: latest (default)";
    const showChips = Array.isArray(algoFacets) && algoFacets.length > 1;

    return (
        <div data-generation-picker className="relative shrink-0">
            <HoverTooltip label={tooltip} placement="left">
                <button
                    ref={triggerRef}
                    type="button"
                    aria-haspopup="listbox"
                    aria-expanded={open}
                    aria-label={tooltip}
                    onClick={() => {
                        if (!open && !pinnedId) notifyGenerationPinChange();
                        setOpen((v) => !v);
                    }}
                    className={[
                        "grid h-[38px] w-[34px] shrink-0 place-items-center border-x border-t p-0 [&_svg]:block [&_svg]:h-[13px] [&_svg]:w-[13px]",
                        pinnedId
                            ? "border-[color:var(--green)] bg-[color:var(--green-soft)] text-[color:var(--green)]"
                            : "border-[color:var(--line)] bg-[color:var(--control-bg)] text-[color:var(--muted)]",
                    ].join(" ")}
                >
                    <GenerationIcon />
                </button>
            </HoverTooltip>

            {open ? createPortal(
                <div
                    ref={menuRef}
                    data-generation-menu
                    style={{
                        position: "fixed",
                        left: menuPos.left,
                        top: menuPos.top,
                        minWidth: menuPos.minWidth,
                    }}
                    className="z-[1000] grid max-h-[60vh] overflow-y-auto border border-[color:var(--line)] bg-[color:var(--panel)] shadow-lg"
                >
                    <div className="flex items-end border-b border-[color:var(--line)]">
                        <TabButton label="Topics" active={tab === "clusters"} onSelect={() => setTab("clusters")} />
                        <TabButton label="Census" active={tab === "census"} onSelect={() => setTab("census")} />
                    </div>
                    {tab === "census" ? <CensusPanel canManage={canManage} /> : (<>
                    <div className="grid gap-[2px] px-[10px] py-2">
                        <span className={NOTE_CLASS}>
                            Use one topic generation for every query in this browser. Applies immediately.
                        </span>
                    </div>
                    {launchNotice ? (
                        <div className="border-t border-[color:var(--line)] px-[10px] py-2">
                            <span className="text-[11px] text-[color:var(--green)]">{launchNotice}</span>
                        </div>
                    ) : null}
                    {canManage ? <ComposePanel onLaunched={onLaunched} /> : null}
                    {showChips ? (
                        <div className="flex flex-wrap gap-[4px] border-t border-[color:var(--line)] px-[10px] py-2">
                            <AlgoChip label="All" active={selectedAlgo === ALL} onSelect={() => setSelectedAlgo(ALL)} />
                            {algoFacets.map((a) => (
                                <AlgoChip
                                    key={a.algorithm}
                                    label={`${a.algorithm} (${a.count})`}
                                    active={selectedAlgo === a.algorithm}
                                    onSelect={() => setSelectedAlgo(a.algorithm)}
                                />
                            ))}
                        </div>
                    ) : null}
                    <MenuRow
                        title="Latest completed (default)"
                        note="The most recently completed generation from any algorithm."
                        active={!pinnedId}
                        onSelect={() => choose(null)}
                    />
                    {orphanPin ? (
                        <MenuRow
                            title={`${orphanPin.algorithm ?? "generation"} · ${String(orphanPin.generationId).slice(0, 8)}…`}
                            note="Currently pinned. This generation is unavailable in the list."
                            active
                            onSelect={() => choose(null)}
                        />
                    ) : null}
                    {listDenied ? (
                        // Visible only via a set pin (generationPin exception) but the
                        // role lacks analytics access — the query is permission-disabled
                        // and will never resolve, so show a hint, not a forever-spinner.
                        <div className="border-t border-[color:var(--line)] px-[10px] py-2">
                            <span className={NOTE_CLASS}>You don&rsquo;t have analytics access to list generations.</span>
                        </div>
                    ) : error ? (
                        <div className="border-t border-[color:var(--line)] px-[10px] py-2">
                            <span className="text-[11px] text-red-400">{error}</span>
                            <div className={NOTE_CLASS}>
                                Generation listing is not available on this server.
                            </div>
                        </div>
                    ) : listLoading || generations === null ? (
                        <div className={`${NOTE_CLASS} border-t border-[color:var(--line)] px-[10px] py-2`}>Loading generations…</div>
                    ) : (
                        <>
                            {generations.map((g) => {
                                const completed = g.status === "completed";
                                return (
                                    <MenuRow
                                        key={g.generationId}
                                        title={`${g.algorithm} · ${g.generationId.slice(0, 8)}…`}
                                        note={[
                                            completed ? null : g.status,
                                            `${g.clustersCreated} topics`,
                                            `${g.eventsProcessed.toLocaleString()} events`,
                                            completed ? fmtGenerationDate(g.completedAt) : null,
                                        ].filter(Boolean).join(" · ")}
                                        active={g.generationId === pinnedId}
                                        disabled={!completed}
                                        onSelect={() => choose(g)}
                                    />
                                );
                            })}
                            {generations.length === 0 ? (
                                <div className={`${NOTE_CLASS} border-t border-[color:var(--line)] px-[10px] py-2`}>
                                    {selectedAlgo === ALL
                                        ? "No topic generations recorded for this tenant yet."
                                        : `No ${selectedAlgo} generations recorded for this tenant yet.`}
                                </div>
                            ) : null}
                        </>
                    )}
                    </>)}
                </div>,
                document.body,
            ) : null}
        </div>
    );
}
